/**
 * Labstep
 *
 * @module containers/Entity/Delete
 * @desc Container for deleting entities
 */

import type { APICallOptions } from 'labstep-web/models';
import type { EntityName } from 'labstep-web/services/schema/schema';
import { deleteEntity as deleteEntityAction } from 'labstep-web/state/actions/entity';
import { selectDeleteEntityStatus } from 'labstep-web/state/selectors/entity';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  EntityDeleteContainerProps,
  IEntityDeleteContainerChildrenProps,
} from './types';

const Container = <T extends EntityName>({
  children,
  entityName,
  id,
}: {
  children: (
    props: IEntityDeleteContainerChildrenProps,
  ) => React.ReactElement | null;
} & EntityDeleteContainerProps<T>): React.ReactElement | null => {
  const dispatch = useDispatch();

  const status = useSelector((state: any) =>
    selectDeleteEntityStatus(state, entityName, id),
  );

  const deleteEntity = useCallback(
    (options?: APICallOptions, body?: any) => {
      dispatch(deleteEntityAction(entityName, id, options, body));
    },
    [dispatch, entityName, id],
  );

  const props: IEntityDeleteContainerChildrenProps = {
    deleteEntity,
    status,
  };

  return children(props);
};

/**
 * Delete Container connecting a component with state
 * Returns original component with
 * delete function and status of action
 */
export const EntityDeleteContainer = Container;
